import React, { useEffect, useState } from 'react';
import { createRelance } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';

// Local (browser tz) YYYY-MM-DD for today — avoids timezone drift where
// new Date().toISOString() would return yesterday in some locales.
const todayLocalIso = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const shiftDays = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const PRESETS = [
    { label: 'J+3', days: 3 },
    { label: 'J+7', days: 7 },
    { label: 'J+15', days: 15 },
    { label: 'J+30', days: 30 },
];

const initialForm = () => ({
    type_relance: 'email',
    date_prevue: todayLocalIso(),
    message: '',
});

export const RelanceDialog = ({ open, onOpenChange, dossierId, onAdded }) => {
    const [form, setForm] = useState(initialForm);

    // Reset the form (and re-seed today's date) each time the dialog opens
    useEffect(() => {
        if (open) setForm(initialForm());
    }, [open]);

    const handleAdd = async () => {
        if (!form.date_prevue) {
            toast.error('Veuillez sélectionner une date');
            return;
        }
        try {
            // Convert local YYYY-MM-DD to ISO for the backend (noon UTC keeps day intact)
            const isoDate = new Date(`${form.date_prevue}T12:00:00Z`).toISOString();
            await createRelance({ dossier_id: dossierId, ...form, date_prevue: isoDate });
            toast.success('Relance planifiée');
            onOpenChange(false);
            onAdded && onAdded();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur lors de la création');
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Planifier une relance</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label className="field-label">Type de relance</Label>
                        <Select value={form.type_relance} onValueChange={(v) => setForm((prev) => ({ ...prev, type_relance: v }))}>
                            <SelectTrigger className="h-10 rounded-sm mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="email">Email</SelectItem>
                                <SelectItem value="sms">SMS</SelectItem>
                                <SelectItem value="appel">Appel</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="field-label">Date prévue *</Label>
                        <Input
                            type="date"
                            value={form.date_prevue || ''}
                            min={todayLocalIso()}
                            onChange={(e) => setForm((prev) => ({ ...prev, date_prevue: e.target.value }))}
                            className="h-10 rounded-sm mt-1"
                            data-testid="relance-date"
                        />
                        <div className="flex flex-wrap gap-1.5 mt-2">
                            {PRESETS.map((p) => {
                                const iso = shiftDays(p.days);
                                const active = form.date_prevue === iso;
                                return (
                                    <button
                                        key={p.label}
                                        type="button"
                                        onClick={() => setForm((prev) => ({ ...prev, date_prevue: iso }))}
                                        className={`px-2.5 py-1 text-xs font-medium rounded-sm border transition-colors ${
                                            active
                                                ? 'bg-[#E85A24] border-[#E85A24] text-white'
                                                : 'bg-white border-[#E4E4E7] text-[#0A0A0A] hover:border-[#E85A24] hover:text-[#E85A24]'
                                        }`}
                                        data-testid={`relance-preset-${p.label.toLowerCase()}`}
                                    >
                                        {p.label}
                                    </button>
                                );
                            })}
                        </div>
                        <p className="text-xs text-[#52525B] mt-1">La date d&apos;aujourd&apos;hui est présélectionnée — utilisez les raccourcis pour +3/+7/+15/+30 jours.</p>
                    </div>
                    <div>
                        <Label className="field-label">Message (optionnel)</Label>
                        <Textarea
                            value={form.message}
                            onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
                            rows={3}
                            className="rounded-sm mt-1"
                            placeholder="Message à envoyer..."
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
                    <Button onClick={handleAdd} className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white" data-testid="relance-submit">Planifier</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
